import * as React from 'react';
import { FunctionComponent } from 'react';
import propz from 'propz';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import {
  SchoolEvent,
  SchoolEventIndividualData,
  SchoolEventIndividualDiscipline,
  SchoolEventTeamDataPlayer
} from '../../models/event';
import './SchoolEventDisciplineSummary.scss';

interface Props {
  schoolEvent: SchoolEvent;
  allTeamPlayers: (SchoolEventIndividualData | SchoolEventTeamDataPlayer)[];
  individualDiscipline: SchoolEventIndividualDiscipline[];
  onEditEventDisciplineClick: (value, player, oneDisciplineId) => void;
  onEditDisciplineClick?: () => void;
  onSaveDisciplineClick?: () => void;
  isEditDiscipline: boolean;
}

export const SchoolEventDisciplineSummary: FunctionComponent<Props> = props => {
  const { schoolEvent, allTeamPlayers, individualDiscipline, isEditDiscipline } = props;
  const sportDisciplineArray = propz.get(schoolEvent, ['sport', 'discipline'], []);

  const getInitials = (name: string) =>
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(namePart => namePart.charAt(0).toUpperCase())
      .join('');

  const getAvatarUrl = (player: SchoolEventIndividualData | SchoolEventTeamDataPlayer) => {
    return propz.get(player, ['avatar'], '');
  };

  const getPlayerDisciplineValue = (
    player: SchoolEventIndividualData | SchoolEventTeamDataPlayer,
    disciplineId: string
  ) => {
    const pData = individualDiscipline.find(
      pUserData => pUserData.disciplineId === disciplineId && pUserData.userId === player.userId
    );
    return pData ? pData.value : 0;
  };

  const getTotalDisciplinePoints = () => {
    return individualDiscipline.reduce((total, item) => total + Number(item.value || 0), 0);
  };

  const getPlayersWithDisciplineCount = () => {
    return allTeamPlayers.filter(player =>
      sportDisciplineArray.some(discipline => getPlayerDisciplineValue(player, discipline._id) > 0)
    ).length;
  };

  const renderAvatar = (player: SchoolEventIndividualData | SchoolEventTeamDataPlayer, name: string) => {
    const avatar = getAvatarUrl(player);
    return (
      <span className="eEventMeasuresAvatar">
        {avatar ? (
          <img className="eEventMeasuresAvatar__img" src={avatar} alt="" />
        ) : (
          <span className="eEventMeasuresAvatar__fallback">{getInitials(name)}</span>
        )}
      </span>
    );
  };

  const renderPlayerDiscipline = player => {
    return sportDisciplineArray.map(oneDiscipline => {
      const value = getPlayerDisciplineValue(player, oneDiscipline._id);

      return (
        <div key={oneDiscipline._id} className="eEventMeasureItem">
          <div className="eEventMeasureItem__copy">
            <div className="eEventMeasureItem__name">{oneDiscipline.name}</div>
          </div>
          <div className="eEventMeasureItem__control">
            <input
              min="0"
              className="eEventMeasuresNumber"
              type="number"
              value={value}
              disabled={!isEditDiscipline}
              onChange={e => {
                props.onEditEventDisciplineClick(e.target.value, player, oneDiscipline._id);
              }}
            />
          </div>
        </div>
      );
    });
  };

  const rows = allTeamPlayers.map((player, index) => {
    const fullName = getFullName(player);
    return (
      <div key={`discipline_summary_${index}`} className="eEventMeasuresRow">
        <div className="eEventMeasuresRow__student">
          <span className="eEventMeasuresRow__index">{index + 1}</span>
          {renderAvatar(player, fullName)}
          <div className="eEventMeasuresRow__studentCopy">
            <div className="eEventMeasuresRow__name">{fullName}</div>
          </div>
        </div>
        <div className="eEventMeasuresRow__metrics">{renderPlayerDiscipline(player)}</div>
      </div>
    );
  });

  return (
    <div className="eEventMeasures eEventMeasures--discipline">
      <div className="eEventMeasuresHeader">
        <div>
          <h2 className="eEventMeasuresHeader__title">Discipline</h2>
          <div className="eEventMeasuresHeader__subtitle">
            Track discipline points recorded against each participant.
          </div>
        </div>
        <div className="eEventMeasuresHeader__actions">
          {isEditDiscipline ? (
            <button
              type="button"
              className="sit-btn sit-btn--primary sit-btn--sm"
              onClick={props.onSaveDisciplineClick}
              disabled={!props.onSaveDisciplineClick || allTeamPlayers.length === 0}
            >
              Save discipline
            </button>
          ) : (
            <button
              type="button"
              className="sit-btn sit-btn--primary sit-btn--sm"
              onClick={props.onEditDisciplineClick}
              disabled={!props.onEditDisciplineClick || allTeamPlayers.length === 0}
            >
              Edit discipline
            </button>
          )}
        </div>
      </div>

      <div className="eEventMeasuresSummary">
        <div className="eEventMeasuresSummaryChip eEventMeasuresSummaryChip--total">
          <span className="eEventMeasuresSummaryChip__dot" />
          <span className="eEventMeasuresSummaryChip__label">Participants</span>
          <span className="eEventMeasuresSummaryChip__value">{allTeamPlayers.length}</span>
        </div>
        <div className="eEventMeasuresSummaryChip eEventMeasuresSummaryChip--metrics">
          <span className="eEventMeasuresSummaryChip__dot" />
          <span className="eEventMeasuresSummaryChip__label">Types</span>
          <span className="eEventMeasuresSummaryChip__value">{sportDisciplineArray.length}</span>
        </div>
        <div className="eEventMeasuresSummaryChip eEventMeasuresSummaryChip--discipline">
          <span className="eEventMeasuresSummaryChip__dot" />
          <span className="eEventMeasuresSummaryChip__label">Total points</span>
          <span className="eEventMeasuresSummaryChip__value">{getTotalDisciplinePoints()}</span>
        </div>
        <div className="eEventMeasuresSummaryChip eEventMeasuresSummaryChip--complete">
          <span className="eEventMeasuresSummaryChip__dot" />
          <span className="eEventMeasuresSummaryChip__label">Students</span>
          <span className="eEventMeasuresSummaryChip__value">{getPlayersWithDisciplineCount()}</span>
        </div>
      </div>

      <div className="eEventMeasuresList">
        {rows.length > 0 ? rows : <div className="eEventMeasuresEmpty">No participants</div>}
      </div>
    </div>
  );
};

SchoolEventDisciplineSummary.displayName = 'SchoolEventDisciplineSummary';
